//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useConfig } from '../../Config';
import { useTeamspaceContext } from '../../TeamspaceContext';
import { useUserAuth } from '../../UserAuth';
import TenantDataService from '../../utils/TenantDataService';
import {
  fetchWikiStatus,
  getCachedWikiStatus,
} from '../../utils/wikiStatusCache';
import SwitcherIframe from '../switcher/SwitcherIframe';
import { BookX } from 'lucide-react';

interface SolutionEntry {
  name: string;
  path: string;
  routePath?: string;
}

const WikiPage: React.FC = () => {
  const { t } = useTranslation();
  const { solutions } = useConfig() as { solutions?: SolutionEntry[] };
  const { teamspaceName, teamspaceId } = useTeamspaceContext();
  const { user, isAuthenticated } = useUserAuth();
  const tenantDataService = useMemo(
    () =>
      new TenantDataService(
        () => user,
        () => isAuthenticated,
      ),
    [user, isAuthenticated],
  );

  // Wiki availability is checked lazily for only the active teamspace (and
  // cached/shared with the sidebar nav check) instead of being enriched
  // eagerly for every teamspace the user belongs to.
  const [wikiFetchResult, setWikiFetchResult] = useState<
    { teamspaceId: string; hasWiki: boolean } | undefined
  >(undefined);
  const cachedHasWiki = teamspaceId
    ? getCachedWikiStatus(teamspaceId)
    : undefined;
  // Derived during render; the effect below only setState()s from its async callback.
  const hasWiki =
    cachedHasWiki ??
    (wikiFetchResult && wikiFetchResult.teamspaceId === teamspaceId
      ? wikiFetchResult.hasWiki
      : undefined);

  useEffect(() => {
    if (!teamspaceId || getCachedWikiStatus(teamspaceId) !== undefined) {
      return;
    }
    let cancelled = false;
    fetchWikiStatus(teamspaceId, () =>
      tenantDataService.getTeamspaceWikiStatus(teamspaceId),
    ).then((result) => {
      if (!cancelled) {
        setWikiFetchResult({ teamspaceId, hasWiki: result });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [teamspaceId, tenantDataService]);

  const controlBasePath = (
    (solutions as SolutionEntry[] | undefined)?.find(
      (s) => s.name === 'Control',
    )?.path ?? ''
  ).replace(/\/$/, '');

  const iframeSrc =
    controlBasePath && teamspaceName
      ? `${controlBasePath}/${teamspaceName}/.devopsconfig/wiki?embed=true`
      : '';

  if (!controlBasePath) {
    return (
      <div data-testid="wiki-page" className="p-6">
        <div
          role="alert"
          data-testid="wiki-page-error"
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {t('wiki.errorNoControl')}
        </div>
      </div>
    );
  }

  if (!teamspaceName) {
    return (
      <div data-testid="wiki-page" className="p-6">
        <div
          role="alert"
          data-testid="wiki-page-error"
          className="rounded border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          {t('wiki.errorNoTeamspace')}
        </div>
      </div>
    );
  }

  if (hasWiki === false) {
    return (
      <div
        data-testid="wiki-page"
        className="flex h-[calc(100vh-4rem)] flex-col items-center justify-center gap-4 p-6"
      >
        <div
          data-testid="wiki-page-unavailable"
          className="flex max-w-md flex-col items-center text-center"
        >
          <div className="mb-4 flex size-16 items-center justify-center rounded-full bg-muted">
            <BookX className="size-8 text-muted-foreground" />
          </div>
          <h2 className="text-xl font-semibold text-foreground">
            {t('wiki.unavailableHeading')}
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {t('wiki.unavailableDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="wiki-page">
      <SwitcherIframe
        iframeSrc={iframeSrc}
        appName="Wiki"
        path="/wiki"
        switcherPosition="right"
        syncBrowserUrl={false}
        openExternalLinksInNewTab
      />
    </div>
  );
};

export default WikiPage;
